const getFaviconMimeType = (href: string): string | undefined => {
    const sanitizedHref = href.split('#')[0]?.split('?')[0] ?? href;
    const extension = sanitizedHref.split('.').pop()?.toLowerCase();

    switch (extension) {
        case 'svg':
            return 'image/svg+xml';
        case 'png':
            return 'image/png';
        case 'ico':
            return 'image/x-icon';
        default:
            return undefined;
    }
};

const setFaviconLink = (head: HTMLHeadElement, href: string, mimeType?: string) => {
    // Remove all existing favicon links — replacing (not updating) is needed
    // because Chrome does not reliably re-fetch when only the href changes.
    const iconLinks = document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon']");
    iconLinks.forEach((link) => {
        // Revoke old blob URLs to prevent memory leaks.
        if (link.href.startsWith('blob:')) {
            URL.revokeObjectURL(link.href);
        }
        link.remove();
    });

    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    if (mimeType) {
        link.type = mimeType;
    }
    head.appendChild(link);
};

export const refreshFavicon = (href: string, signal?: AbortSignal) => {
    if (typeof document === 'undefined') {
        return;
    }

    const head = document.head ?? document.getElementsByTagName('head')[0];
    if (!head) {
        return;
    }

    const mimeType = getFaviconMimeType(href);
    const isSameOrigin = href.startsWith(window.location.origin) || href.startsWith('/');

    if (isSameOrigin) {
        // For same-origin favicons, fetch and set as a blob URL so Chrome is
        // forced to display it, bypassing its aggressive favicon cache.
        fetch(href, signal ? { signal } : undefined)
            .then((res) => {
                if (signal?.aborted) return;
                if (!res.ok) {
                    setFaviconLink(head, href, mimeType);
                    return;
                }
                return res.blob();
            })
            .then((blob) => {
                if (!blob || signal?.aborted) return;
                const blobUrl = URL.createObjectURL(blob);
                setFaviconLink(head, blobUrl, mimeType);
            })
            .catch((err) => {
                // Silently ignore aborted fetches (effect cleanup).
                if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
                setFaviconLink(head, href, mimeType);
            });
    } else {
        // For cross-origin favicons (e.g. static.40seas.com), set the href
        // directly on the <link> element. A fetch() would fail CORS, but the
        // browser loads <link rel="icon"> in no-cors mode natively.
        setFaviconLink(head, href, mimeType);
    }
};
